// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/ReviewPreflight.test.tsx
import { CaretDown } from "@phosphor-icons/react";
import { Checkbox, Text } from "@radix-ui/themes";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { QuestionFormCard } from "./QuestionFormCard";
import {
  REVIEW_PREFLIGHT_GROUPS,
  type ReviewPreflightRow,
  reviewPreflightRowCopy,
} from "./reviewPreflightCopy";

import {
  agentComponentIdAttr,
  CHAT_ATOM_AGENT_COMPONENT_IDS,
} from "../agentComponents/chatAtomRegistry";

export type ReviewPreflightCategory = ReviewPreflightRow & {
  /** Nested under a parent row (Figma scope: Nutrition / Product / Region). */
  indent?: boolean;
  /** Shown but never ticked: an expert method still waiting on its documents. */
  disabled?: boolean;
  /** Shown ticked and never unticked: the rules Experts taught run in every review. */
  alwaysOn?: boolean;
  /**
   * What an Expert row is, written by warhol's code. A row with one keeps its
   * subheading and description behind a details toggle, collapsed by default.
   */
  subheading?: string;
};

export type ReviewPreflightProps = {
  variant?: "confirm" | "scope";
  categories: ReviewPreflightCategory[];
  selectedCategoryIds?: string[];
  onToggleCategory?: (id: string) => void;
  onStart: () => void;
  onChangeScope: () => void;
  onCancel?: () => void;
  summary?: string;
  /** File names as stored. When either is set, the heading ignores `summary`. */
  artworkName?: string;
  specName?: string;
  description?: string;
  /** The always-offered Other row; the focus field shows while it is checked. */
  otherChecked?: boolean;
  onToggleOther?: () => void;
  focusValue?: string;
  onFocusChange?: (value: string) => void;
  testIdPrefix?: string;
  /** Gray out controls after Start or while a send is in flight. */
  disabled?: boolean;
  /** Persisted submit — keeps Start and checkboxes disabled after refresh. */
  submitted?: boolean;
  /** False on history turns; checkboxes and Start ignore input when false. */
  interactive?: boolean;
};

const PANEL_CLASS =
  "flex flex-col gap-2 rounded-xl border border-puntt-neutral-gray-4 bg-puntt-neutral-gray-2 p-2";

/**
 * Pre-review confirm / scope picker. Customer-facing scope copy only — no fake
 * progress chrome (Figma ReviewPreflight 49613:704).
 */
export function ReviewPreflight({
  variant = "confirm",
  categories,
  selectedCategoryIds = [],
  onToggleCategory,
  onStart,
  onChangeScope,
  onCancel,
  summary,
  artworkName,
  specName,
  description,
  otherChecked = false,
  onToggleOther,
  focusValue,
  onFocusChange,
  testIdPrefix = "review-preflight",
  disabled = false,
  submitted = false,
  interactive = true,
}: ReviewPreflightProps) {
  const { t, i18n } = useTranslation("ui");
  const selected = categories.filter((c) => selectedCategoryIds.includes(c.id));
  const confirmTitle =
    summary ??
    (selected
      .map((c) => reviewPreflightRowCopy(c, t, i18n.language).name)
      .join(", ") ||
      t("reviewPreflight.title"));
  const confirmDescription =
    description ?? t("reviewPreflight.confirmDescription");
  const namedHeading =
    artworkName && specName
      ? t("reviewPreflight.scopeHeading", {
          artwork: artworkName,
          spec: specName,
        })
      : artworkName
        ? t("reviewPreflight.scopeHeadingArtwork", { artwork: artworkName })
        : null;
  // why: `summary` is the model's `title`. The heading is the catalog plus
  // the file names, so an English title cannot outrank the saved locale.
  const scopeTitle = namedHeading ?? summary ?? t("reviewPreflight.scopeTitle");
  // A form with no standard row is an artwork-only review. Its line is a
  // locale string, keyed by that shape, not by a language field.
  // why always-on rows are left out: the Expert rules row has no group and
  // runs with or without a spec, so it never makes a form a full review.
  const pickable = categories.filter((category) => category.alwaysOn !== true);
  const artworkOnly =
    // Stryker disable next-line ConditionalExpression: an empty pickable list makes the every() clauses below vacuously true, so forcing this operand false returns the same artworkOnly; forcing it true is not equivalent and the standard-review scope-line test kills it.
    pickable.length === 0 ||
    (pickable.every((category) => category.group != null) &&
      pickable.every((category) => category.group !== "standard"));
  const scopeDescription =
    description ??
    (artworkOnly
      ? t("reviewPreflight.artworkOnlyDescription")
      : t("reviewPreflight.scopeDescription"));
  const isScope = variant === "scope";
  const controlsLocked = !interactive || disabled || submitted;
  const detailsIdBase = useId();
  const [openDetailIds, setOpenDetailIds] = useState<string[]>([]);
  const toggleDetails = (id: string) =>
    setOpenDetailIds((current) =>
      current.includes(id)
        ? current.filter((one) => one !== id)
        : [...current, id],
    );
  // why: zero rows is an artwork with no spec, so Start still runs the text
  // checks. A form that has rows stays locked until one is checked.
  const startLocked =
    controlsLocked ||
    (categories.length > 0 && selectedCategoryIds.length === 0);
  const grouped = categories.some((category) => category.group != null);
  const renderRow = (category: ReviewPreflightCategory) => {
    const copy = reviewPreflightRowCopy(category, t, i18n.language);
    const alwaysOn = category.alwaysOn === true;
    const rowLocked = controlsLocked || category.disabled === true || alwaysOn;
    const checked =
      alwaysOn ||
      (category.disabled !== true && selectedCategoryIds.includes(category.id));
    const layout = category.indent
      ? "flex items-center gap-2 pl-6"
      : "flex items-center gap-2";
    const subheading = category.subheading ?? "";
    const detailsOpen = openDetailIds.includes(category.id);
    const detailsId = `${detailsIdBase}-${category.id}-details`;
    const rowTestId = `${testIdPrefix}-row-${category.id}`;
    const rowLayout = subheading ? `${layout} min-w-0 flex-1` : layout;
    const row = (
      <label
        key={category.id}
        data-testid={rowTestId}
        aria-label={copy.name}
        aria-disabled={category.disabled === true ? true : undefined}
        className={
          category.disabled === true
            ? `${rowLayout} cursor-not-allowed opacity-60`
            : alwaysOn
              ? `${rowLayout} cursor-default`
              : `${rowLayout} cursor-pointer`
        }
      >
        <Checkbox
          checked={checked}
          disabled={rowLocked}
          onCheckedChange={() => {
            // Stryker disable next-line ConditionalExpression: the Checkbox above carries disabled={rowLocked}, so Radix never fires onCheckedChange for a locked row and forcing this guard true changes nothing; forcing it false blocks every toggle, which the enabled-row toggle tests kill.
            if (!rowLocked) onToggleCategory?.(category.id);
          }}
          aria-label={copy.name}
          size="2"
        />
        <span className="flex min-w-0 flex-col">
          <Text size="2" className="text-puntt-neutral-gray-12">
            {copy.name}
          </Text>
          {!subheading && copy.description ? (
            <Text size="1" className="text-puntt-neutral-gray-11">
              {copy.description}
            </Text>
          ) : null}
        </span>
      </label>
    );
    if (!subheading) return row;
    // why the toggle sits outside the label: a click inside the label reaches
    // the checkbox, and opening the details must never tick or untick the row.
    return (
      <div key={category.id} className="flex flex-col gap-1">
        <div className="flex items-start gap-1">
          {row}
          <button
            type="button"
            data-testid={`${rowTestId}-details-toggle`}
            aria-label={t(
              detailsOpen
                ? "reviewPreflight.hideDetails"
                : "reviewPreflight.showDetails",
              { label: copy.name },
            )}
            aria-expanded={detailsOpen}
            aria-controls={detailsId}
            onClick={() => toggleDetails(category.id)}
            className="flex size-5 shrink-0 items-center justify-center rounded text-puntt-neutral-gray-11 hover:bg-puntt-neutral-gray-3"
          >
            <CaretDown
              size={14}
              aria-hidden="true"
              className={`transition-transform ${detailsOpen ? "" : "-rotate-90"}`}
            />
          </button>
        </div>
        <div
          id={detailsId}
          data-testid={`${rowTestId}-details`}
          hidden={!detailsOpen}
          className={detailsOpen ? "flex flex-col gap-0.5 pl-6" : "hidden"}
        >
          <Text
            size="1"
            data-testid={`${rowTestId}-subheading`}
            className="font-medium uppercase tracking-wide text-puntt-neutral-gray-10"
          >
            {subheading}
          </Text>
          {copy.description ? (
            <Text size="1" className="text-puntt-neutral-gray-11">
              {copy.description}
            </Text>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <div
      {...agentComponentIdAttr(CHAT_ATOM_AGENT_COMPONENT_IDS.reviewPreflight)}
      data-testid={testIdPrefix}
      className={PANEL_CLASS}
    >
      <QuestionFormCard
        chrome="inner"
        testId={`${testIdPrefix}-form`}
        testIdPrefix={testIdPrefix}
        detail1={isScope ? scopeTitle : confirmTitle}
        detail2={isScope ? scopeDescription : confirmDescription}
        primary={{
          action: "start",
          testId: `${testIdPrefix}-start`,
          label: t("reviewPreflight.startReview"),
          onClick: onStart,
          disabled: startLocked,
        }}
        tertiary={
          isScope
            ? onCancel
              ? {
                  action: "cancel",
                  testId: `${testIdPrefix}-cancel`,
                  label: t("reviewPreflight.cancel"),
                  onClick: onCancel,
                  disabled: controlsLocked,
                }
              : undefined
            : {
                action: "change-scope",
                testId: `${testIdPrefix}-change-scope`,
                label: t("reviewPreflight.changeScope"),
                onClick: onChangeScope,
                disabled: controlsLocked,
              }
        }
      >
        {isScope ? (
          <div className="flex flex-col gap-2">
            {grouped ? (
              <>
                {/* why ungrouped rows come first: the always-on Expert rules
                    row has no group, and a grouped form must still show it. */}
                {categories
                  .filter((category) => category.group == null)
                  .map((category) => renderRow(category))}
                {REVIEW_PREFLIGHT_GROUPS.map((group) => {
                  const rows = categories.filter(
                    (category) => category.group === group,
                  );
                  if (rows.length === 0) return null;
                  const heading = t(`reviewPreflight.groups.${group}`);
                  return (
                    <section
                      key={group}
                      data-testid={`${testIdPrefix}-group-${group}`}
                      aria-label={heading}
                      className="flex flex-col gap-2"
                    >
                      <h4 className="m-0 text-[13px] font-semibold text-puntt-neutral-gray-12">
                        {heading}
                      </h4>
                      {rows.map((category) => renderRow(category))}
                    </section>
                  );
                })}
              </>
            ) : (
              categories.map((category) => renderRow(category))
            )}
            <label
              data-testid={`${testIdPrefix}-scope-other`}
              className="flex cursor-pointer items-center gap-2"
            >
              <Checkbox
                checked={otherChecked}
                disabled={controlsLocked}
                onCheckedChange={() => {
                  if (!controlsLocked) onToggleOther?.();
                }}
                aria-label={t("reviewPreflight.other")}
                size="2"
              />
              <Text size="2" className="text-puntt-neutral-gray-12">
                {t("reviewPreflight.other")}
              </Text>
            </label>
            {otherChecked ? (
              <div className="flex flex-col gap-2 pl-6">
                <Text size="1" className="text-puntt-neutral-gray-11">
                  {t("reviewPreflight.focusLabel")}
                </Text>
                <input
                  data-testid={`${testIdPrefix}-focus`}
                  aria-label={t("reviewPreflight.focusLabel")}
                  placeholder={t("reviewPreflight.focusPlaceholder")}
                  value={focusValue ?? ""}
                  disabled={controlsLocked}
                  onChange={(e) => onFocusChange?.(e.target.value)}
                  className="h-9 w-full rounded-lg border border-puntt-neutral-gray-4 bg-base-white px-2 text-[14px] text-puntt-neutral-gray-12 outline-none focus:border-puntt-accent-8 disabled:cursor-not-allowed disabled:opacity-60"
                />
              </div>
            ) : null}
          </div>
        ) : null}
      </QuestionFormCard>
    </div>
  );
}
